// AdrenalinePanel — "Who wins — spotlight & fairness" (Phase B, plan §4.7).
//
// ONE preset decides WHO gets the memorable wins (spotlight bursts, comebacks
// for players on a bad run, a little extra swing for everyone). It NEVER
// changes how much the house keeps — that is the Master casino profit preset
// rendered directly above this panel in the Earnings tab (owner rule:
// consolidate, don't scatter → same shell, same width, same button, and the
// hub lifts our GET so the master panel's single "Now active:" line can read
// "Fair Play · Spotlight: Normal").
//
// Backend contract (Phase B §B5): GET/PUT /admin/platform/adrenaline_preset.
//   GET → { preset, key, custom, bundle, consequence:{off,low,normal,high[,custom]},
//           live:{burst,n,N,dent,districtsKnownPct,mode:'sim'|'live',
//                 goldenMinActive, byPreset:{low,normal,high[,custom]:{N,dent,gapWeeks}}} }
//   GET ?custom=<int> → the same, with `consequence.custom` written for THAT value.
// The consequence sentences are GENERATED server-side from live 7-day data
// (plan §4.7: burst = median fired amount of the last 4 windows, n = active
// districts, dent = share sum) — so this file holds NO explanatory copy of its
// own beyond loading/fallback text. The Custom chip's text comes, in order,
// from: (1) a debounced `?custom=<v>` preview for the typed value, (2) the plain
// GET's `consequence.custom` — which the server bakes for the STORED custom
// value only, so it is used only when the typed value equals the stored one,
// (3) a sentence composed here from the NORMAL bundle's live numbers scaled by
// v/100 (the top-level `live.dent` is the CURRENT preset's — zero while Off —
// so it must not be used for that).
//
// Phase B is DARK: the preset ships 'off' and the backend treats off as
// byte-identical behaviour. This panel is the only place it can be changed.

import { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import ExplainedSetting from '@baaligames/shared-ui/admin/components/ExplainedSetting';

export type AdrenalineKey = 'off' | 'low' | 'normal' | 'high' | 'custom';

export type AdrenalineLivePreset = { N?: number | null; dent?: number | null; gapWeeks?: number | null; v?: number | null };

export type AdrenalineLive = {
  burst?: number | null;            // ₹ typical spotlight win (median of last 4 windows, or sim)
  n?: number | null;                // districts that qualify for a spotlight (≥ goldenMinActive players)
  N?: number | null;                // bursts per week — for the CURRENTLY stored preset (0 while Off)
  dent?: number | null;             // % ordinary sessions run tighter — CURRENT preset (0 while Off)
  districtsKnownPct?: number | null;
  mode?: 'sim' | 'live' | string;
  goldenMinActive?: number | null;  // players a district needs before a spotlight is scheduled
  activeDistricts?: number | null;
  byPreset?: Partial<Record<'low' | 'normal' | 'high' | 'custom', AdrenalineLivePreset>> | null;
};

/** Defensive shape of GET /admin/platform/adrenaline_preset (written in parallel by WP5a). */
export type AdrenalinePresetGet = {
  preset?: unknown;                 // raw stored value: 'off'|'low'|'normal'|'high'|{custom:int}
  key?: string;
  custom?: number | null;
  bundle?: unknown;
  consequence?: Partial<Record<AdrenalineKey, string>> | null;
  live?: AdrenalineLive | null;
};

export type AdrenalineState = {
  data: AdrenalinePresetGet | null;
  loading: boolean;
  err: string;
  reload: () => Promise<void>;
};

const KEYS: AdrenalineKey[] = ['off', 'low', 'normal', 'high', 'custom'];
const LABEL: Record<AdrenalineKey, string> = {
  off: 'Off', low: 'Low', normal: 'Normal', high: 'High', custom: 'Custom',
};

/** The effective preset key, derived from `key` or (fallback) the raw stored value. */
export function resolveAdrenalineKey(d: AdrenalinePresetGet | null | undefined): AdrenalineKey | null {
  if (!d) return null;
  const k = String(d.key ?? '').toLowerCase();
  if ((KEYS as string[]).includes(k)) return k as AdrenalineKey;
  const p = d.preset;
  if (typeof p === 'string' && (KEYS as string[]).includes(p.toLowerCase())) return p.toLowerCase() as AdrenalineKey;
  if (p && typeof p === 'object' && 'custom' in (p as Record<string, unknown>)) return 'custom';
  return null;
}

/** The custom percentage (0..200) when the preset is custom; null otherwise. */
export function resolveAdrenalineCustom(d: AdrenalinePresetGet | null | undefined): number | null {
  if (!d) return null;
  const direct = Number(d.custom);
  if (Number.isFinite(direct) && d.custom != null) return direct;
  const p = d.preset;
  if (p && typeof p === 'object' && 'custom' in (p as Record<string, unknown>)) {
    const v = Number((p as Record<string, unknown>).custom);
    if (Number.isFinite(v)) return v;
  }
  return null;
}

/** "Spotlight: Normal" — the fragment the master panel appends to its "Now active:" line. */
export function spotlightActiveLabel(state: AdrenalineState): string {
  if (state.loading && !state.data) return 'Spotlight: …';
  // GET failed (e.g. backend not deployed yet): say nothing rather than
  // claiming "Off" on the master panel's headline line.
  if (!state.data) return '';
  const key = resolveAdrenalineKey(state.data);
  if (!key) return 'Spotlight: Off';
  if (key === 'custom') {
    const v = resolveAdrenalineCustom(state.data);
    return v === 0 ? 'Spotlight: Off (custom 0%)' : `Spotlight: Custom ${v ?? '?'}%`;
  }
  return `Spotlight: ${LABEL[key]}`;
}

/** Fetches the preset once (when `enabled`) and exposes a reload. Lifted by the hub. */
export function useAdrenalinePreset(enabled = true): AdrenalineState {
  const [data, setData] = useState<AdrenalinePresetGet | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const d = await adminApi.get<AdrenalinePresetGet>('/admin/platform/adrenaline_preset');
      setData(d ?? null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not load the spotlight setting.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    (async () => {
      setLoading(true); setErr('');
      try {
        const d = await adminApi.get<AdrenalinePresetGet>('/admin/platform/adrenaline_preset');
        if (alive) setData(d ?? null);
      } catch (e) {
        if (alive) setErr(e instanceof ApiError ? e.message : 'Could not load the spotlight setting.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [enabled]);

  return { data, loading, err, reload };
}

const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const clampCustom = (v: unknown): number | null => {
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(200, n));
};

/**
 * The Custom chip's consequence text for value `v` (pure; exported for the smoke test).
 * Source order: `preview` (the server's `?custom=v` sentence) → the plain GET's
 * `consequence.custom`, valid ONLY when `v` is the stored custom value (the server
 * bakes that value in; there is no placeholder) → composed from Normal's numbers
 * scaled by v/100 (plan §4.7). Never `live.dent`: that is the CURRENT preset's
 * figure and reads 0 while Off.
 */
export function describeCustomText(args: {
  v: number;
  cons: Partial<Record<AdrenalineKey, string>> | null | undefined;
  live: AdrenalineLive | null | undefined;
  preview?: string | null;
  currentCustom?: number | null;
}): string {
  const { v, cons, live, preview, currentCustom } = args;
  if (v === 0) return cons?.off ? `0% behaves exactly like Off — ${cons.off}` : '0% behaves exactly like Off.';
  if (preview && String(preview).trim()) return String(preview);
  const stored = cons?.custom;
  if (stored && String(stored).trim() && (currentCustom === v || String(stored).includes('{v}'))) {
    return String(stored).replace(/\{v\}/g, String(v));
  }
  const burst = live?.burst != null ? Number(live.burst) : null;
  const n = live?.n != null ? Number(live.n) : null;
  const normal = live?.byPreset?.normal ?? null;
  const normalDent = normal?.dent != null && Number.isFinite(Number(normal.dent)) ? Number(normal.dent) : 6;
  const gapWeeks = normal?.gapWeeks != null && Number.isFinite(Number(normal.gapWeeks)) ? Number(normal.gapWeeks) : 3;
  const minActive = live?.goldenMinActive != null ? Number(live.goldenMinActive) : null;
  const parts: string[] = [`Custom ${v}% — the Normal setting scaled to ${v}%: spotlight budget, comebacks and swing all × ${v / 100}. Same house profit.`];
  if (burst != null && Number.isFinite(burst)) {
    const where = n != null && n > 0
      ? `in each of the ${n} active district${n === 1 ? '' : 's'}`
      : `in each active district${minActive ? ` (none has ${minActive} active players yet)` : ''}`;
    parts.push(`A spotlight win of about ${inr(burst)} ${where} at least every ${gapWeeks} week${gapWeeks === 1 ? '' : 's'}.`);
  }
  parts.push(`Ordinary sessions run about ${(normalDent * v / 100).toFixed(1)}% tighter.`);
  if (live?.mode === 'sim') parts.push('(Projected — no spotlight history yet.)');
  return parts.join(' ');
}

export default function AdrenalinePanel({ state }: { state: AdrenalineState }) {
  const { data, loading, err: loadErr, reload } = state;
  // The control's value: a preset key string, or a number when Custom.
  // Seeded from the GET if it is already there (re-mounts), else by the effect.
  const [selected, setSelected] = useState<string | number>(() => {
    const k = resolveAdrenalineKey(data);
    return k === 'custom' ? (resolveAdrenalineCustom(data) ?? 100) : (k ?? 'off');
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Seed the control from the server value whenever the GET lands.
  useEffect(() => {
    const key = resolveAdrenalineKey(data);
    if (!key) return;
    if (key === 'custom') setSelected(resolveAdrenalineCustom(data) ?? 100);
    else setSelected(key);
  }, [data]);

  const live = data?.live ?? null;
  const cons = data?.consequence ?? null;
  const currentKey = resolveAdrenalineKey(data);
  const currentCustom = resolveAdrenalineCustom(data);

  // Chip consequence text comes ONLY from the GET (generated from live data);
  // the fallback strings are deliberately non-explanatory.
  const presets = useMemo(() => {
    const t = (k: AdrenalineKey) => {
      const s = cons?.[k];
      return s && String(s).trim() ? String(s) : (loading ? 'Loading the explanation…' : 'Explanation unavailable — the spotlight service did not return one.');
    };
    return [
      { value: 'off',    label: 'Off',    tagline: 'plain chance', tone: 'neutral' as const,     consequence: t('off') },
      { value: 'low',    label: 'Low',    tagline: 'gentle',       tone: 'safe' as const,        consequence: t('low') },
      { value: 'normal', label: 'Normal', tagline: 'balanced',     tone: 'recommended' as const, consequence: t('normal') },
      { value: 'high',   label: 'High',   tagline: 'bold',         tone: 'risky' as const,       consequence: t('high') },
    ];
  }, [cons, loading]);

  // Exact server sentence for a TYPED custom value: the route accepts
  // `?custom=<int>` and returns `consequence.custom` written for that value.
  // Debounced while the owner types; cached per value for this mount. If the
  // preview fails (older backend), the locally composed sentence stands.
  const [customPreview, setCustomPreview] = useState<Record<number, string>>({});
  const previewValue = typeof selected === 'number' ? clampCustom(selected) : null;
  useEffect(() => {
    if (previewValue === null || previewValue === 0) return;
    if (customPreview[previewValue] !== undefined) return;
    if (previewValue === currentCustom && cons?.custom) return;   // the plain GET already carries it
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const d = await adminApi.get<AdrenalinePresetGet>(`/admin/platform/adrenaline_preset?custom=${previewValue}`);
        const txt = d?.consequence?.custom;
        if (alive && txt && String(txt).trim()) setCustomPreview((m) => ({ ...m, [previewValue]: String(txt) }));
      } catch { /* keep the locally composed sentence */ }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [previewValue, currentCustom, cons, customPreview]);

  // Custom = the Normal bundle scaled by v/100 (plan §4.7). Text source order:
  // preview for this value → stored-value sentence (only when v IS the stored
  // value; it has no placeholder) → composed here from Normal's live numbers.
  const describeCustom = useCallback((raw: unknown) => {
    const v = clampCustom(raw);
    if (v === null) return 'Type a number from 0 to 200. 100 = Normal, 0 = Off, 200 = twice Normal\'s spotlight budget and swing.';
    return describeCustomText({ v, cons, live, preview: customPreview[v] ?? null, currentCustom });
  }, [cons, live, customPreview, currentCustom]);

  const formatValue = (v: unknown) => {
    if (typeof v === 'number') return v === 0 ? 'Off (custom 0%)' : `Custom ${v}%`;
    const k = String(v) as AdrenalineKey;
    return LABEL[k] ?? String(v);
  };

  async function apply() {
    setMsg(''); setErr('');
    const isCustom = typeof selected === 'number' || selected === '';
    const custom = isCustom ? clampCustom(selected) : null;
    if (isCustom && custom === null) { setErr('Enter a number from 0 to 200 for Custom.'); return; }
    const key: AdrenalineKey = isCustom ? 'custom' : (String(selected) as AdrenalineKey);
    const label = isCustom ? `Custom ${custom}%` : LABEL[key];
    const explain = isCustom ? describeCustom(custom) : (presets.find((p) => p.value === key)?.consequence ?? '');
    const turningOn = (currentKey === 'off' || currentKey === null || (currentKey === 'custom' && currentCustom === 0))
      && !(key === 'off' || (isCustom && custom === 0));
    const warn = turningOn
      ? '\n\nThis switches the spotlight ON: a small weekly give-back budget starts being spent on who wins. House profit stays exactly as set by Master casino profit.'
      : '';
    if (!window.confirm(`Apply "${label}" for who wins?${warn}\n\n${explain}`)) return;
    setSaving(true);
    try {
      const body = isCustom ? { preset: { custom } } : { preset: key };
      await adminApi.put('/admin/platform/adrenaline_preset', body);
      await reload();
      setMsg(`Applied "${label}".`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Apply failed.');
    } finally { setSaving(false); }
  }

  const nowLabel = currentKey
    ? (currentKey === 'custom' ? formatValue(currentCustom ?? 0) : LABEL[currentKey])
    : (loading ? '…' : 'Off');

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <header className="space-y-1">
        <h1 className="text-xl font-bold text-arena-900 dark:text-white flex items-center gap-2">
          <Icon name="auto_awesome" size={20} /> Who wins — spotlight &amp; fairness
        </h1>
        <p className="text-[13px] text-arena-500 dark:text-neon-200/70">
          One setting for who gets the memorable wins: a spotlight win in every active district,
          a visible comeback for anyone on a bad run, and a bit more swing for everyone.
        </p>
        <p className="text-[12px] font-semibold text-arena-600 dark:text-neon-200/80">
          Now active: <span className="text-violet-600 dark:text-violet-300">{nowLabel}</span>
        </p>
      </header>

      {/* Banner — the one sentence the owner must never forget (plan §4.7). */}
      <div className="rounded-xl border px-3 py-2.5 text-[12.5px] leading-relaxed flex items-start gap-2
                      bg-emerald-50 border-emerald-300/70 text-emerald-900
                      dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-100">
        <Icon name="shield" size={16} className="shrink-0 mt-0.5" />
        <span>This never changes how much the house keeps — that is set by Master casino profit above.</span>
      </div>

      {/* WHICH GAMES THIS REACHES (Phase 5 item 27, 2026-09-15).
          The program plan required an explicit owner decision here: either the
          head-to-head games get a rake and a per-user edge delta, or the admin UI
          "must say plainly that the adrenaline preset does not touch them". This is
          that plain statement. Adding a rake to a player-vs-player game changes what
          the player is buying and is an owner's call, not an implementation detail —
          so nothing was added silently. The list is written out rather than derived
          so it cannot quietly go stale against ADRENALINE_GAMES without someone
          noticing; scripts/adrenaline-scope-gate.mjs asserts the two agree. */}
      <div className="rounded-xl border px-3 py-2.5 text-[12.5px] leading-relaxed flex items-start gap-2
                      bg-amber-50 border-amber-300/70 text-amber-900
                      dark:bg-amber-500/10 dark:border-amber-400/30 dark:text-amber-100">
        <Icon name="info" size={16} className="shrink-0 mt-0.5" />
        <span>
          <b>This setting reaches 8 games only:</b> Aviator, Andar Bahar, Teen Patti, Roulette,
          Cash Casino, Lucky 7, Plinko and Thimbles — the ones where the house is the opponent.
          <br />
          <b>It does not touch</b> Cricket, Football, Tennis, Rummy, Ludo, Snake &amp; Ladder,
          Carrom, Chess or XO. In those the players are betting against each other or against a
          live market, so there is no house edge for this to shape. Changing it will not alter
          a single result in them.
        </span>
      </div>

      {loading && !data ? (
        <div className="text-[13px] text-arena-500 dark:text-neon-200/60">Loading…</div>
      ) : loadErr && !data ? (
        <div className="rounded-xl px-3 py-2.5 text-[12.5px]
                        bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200/60
                        dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-400/30 flex items-center gap-2 flex-wrap">
          <span>{loadErr}</span>
          <button onClick={() => void reload()} className="ml-auto text-[12px] font-bold underline">Retry</button>
        </div>
      ) : (
        <>
          <ExplainedSetting
            title="Spotlight & fairness"
            help="Off = plain chance inside the house target (today's behaviour). Low, Normal and High spend a small weekly budget — paid by the other players in the same 3-hour slot — on spotlight wins and comebacks. Custom scales Normal from 0% to 200%."
            value={selected}
            onChange={(v) => setSelected(v === '' ? '' : (typeof v === 'number' ? v : String(v)))}
            formatValue={formatValue}
            presets={presets}
            custom={{ kind: 'number', min: 0, max: 200, step: 5, unitSuffix: '% of Normal', placeholder: 'e.g. 150', describe: describeCustom }}
          />

          {/* Where the numbers in the sentences come from — one honest line. */}
          {live && (
            <p className="text-[11.5px] text-arena-500 dark:text-neon-200/60 -mt-2 px-1">
              {live.mode === 'sim'
                ? 'Numbers above are projections — no spotlight win has been recorded yet (or the games had no volume in the last 7 days); they switch to real figures once a spotlight has fired.'
                : 'Numbers above come from the last 7 days of real play and refresh nightly.'}
              {live.districtsKnownPct != null && Number.isFinite(Number(live.districtsKnownPct)) && (
                <> District known for {Math.round(Number(live.districtsKnownPct))}% of active players.</>
              )}
            </p>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={apply}
              disabled={saving}
              className="px-5 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-bold shadow-[0_2px_10px_rgba(139,92,246,0.35)] transition active:scale-95"
            >
              {saving ? 'Applying…' : 'Apply spotlight setting'}
            </button>
            {msg && <span className="text-[13px] text-emerald-600 dark:text-emerald-400">{msg}</span>}
            {(err || (loadErr && data)) && <span className="text-[13px] text-rose-600 dark:text-rose-400">{err || loadErr}</span>}
          </div>
        </>
      )}
    </div>
  );
}
